import React from 'react';
import { Card, Group, Stack, Text, Title } from '@mantine/core';
import { AreaStack } from '@visx/shape';
import { Group as VisxGroup } from '@visx/group';
import { ParentSize } from '@visx/responsive';
import { scaleLinear, scaleTime } from '@visx/scale';
import { AxisBottom, AxisLeft } from '@visx/axis';
import { ActivityHourBucket } from '../../data/PingSummary';

type Props = {
    data: ActivityHourBucket[] | undefined;
};

type StackKey = 'sport' | 'commercial' | 'other';

const SERIES: { key: StackKey; label: string; color: string }[] = [
    { key: 'sport', label: 'Sport', color: '#51cf66' },
    { key: 'commercial', label: 'Commercial', color: '#ffd43b' },
    { key: 'other', label: 'Other', color: '#868e96' },
];

const MARGIN = { top: 10, right: 16, bottom: 28, left: 36 };

const Activity24hChart: React.FC<Props> = ({ data }) => {
    const buckets = data ?? [];
    const hasData = buckets.some((b) => b.sport + b.commercial + b.other > 0);

    return (
        <Card shadow="xs" withBorder padding="md" h="100%">
            <Stack gap="xs">
                <Group justify="space-between" align="baseline">
                    <Title order={5}>Activity, last 24 hours</Title>
                    <Group gap="md">
                        {SERIES.map((s) => (
                            <Group key={s.key} gap={6} wrap="nowrap">
                                <div
                                    style={{
                                        width: 10,
                                        height: 10,
                                        borderRadius: 2,
                                        backgroundColor: s.color,
                                    }}
                                />
                                <Text size="xs" c="dimmed">
                                    {s.label}
                                </Text>
                            </Group>
                        ))}
                    </Group>
                </Group>

                {!hasData ? (
                    <Text c="dimmed" size="sm">
                        No pings in the last 24 hours.
                    </Text>
                ) : (
                    <div style={{ height: 240, width: '100%' }}>
                        <ParentSize>
                            {({ width, height }) => {
                                if (width < 10 || height < 10) return null;

                                const innerW = width - MARGIN.left - MARGIN.right;
                                const innerH = height - MARGIN.top - MARGIN.bottom;

                                const dates = buckets.map((b) => new Date(b.hour));
                                const maxTotal = Math.max(
                                    1,
                                    ...buckets.map((b) => b.sport + b.commercial + b.other),
                                );

                                const xScale = scaleTime<number>({
                                    domain: [dates[0], dates[dates.length - 1]],
                                    range: [0, innerW],
                                });

                                const yScale = scaleLinear<number>({
                                    domain: [0, maxTotal],
                                    range: [innerH, 0],
                                    nice: true,
                                });

                                return (
                                    <svg width={width} height={height}>
                                        <VisxGroup left={MARGIN.left} top={MARGIN.top}>
                                            <AreaStack<ActivityHourBucket>
                                                data={buckets}
                                                keys={SERIES.map((s) => s.key)}
                                                x={(d) => xScale(new Date(d.data.hour)) ?? 0}
                                                y0={(d) => yScale(d[0]) ?? 0}
                                                y1={(d) => yScale(d[1]) ?? 0}
                                            >
                                                {({ stacks, path }) =>
                                                    stacks.map((stack) => {
                                                        const series = SERIES.find((s) => s.key === stack.key);
                                                        return (
                                                            <path
                                                                key={`stack-${stack.key}`}
                                                                d={path(stack) || ''}
                                                                fill={series?.color ?? '#ccc'}
                                                                opacity={0.85}
                                                            />
                                                        );
                                                    })
                                                }
                                            </AreaStack>
                                            <AxisLeft
                                                scale={yScale}
                                                numTicks={4}
                                                stroke="var(--mantine-color-dimmed)"
                                                tickStroke="var(--mantine-color-dimmed)"
                                                tickLabelProps={() => ({
                                                    fill: 'var(--mantine-color-dimmed)',
                                                    fontSize: 10,
                                                    textAnchor: 'end',
                                                    dx: '-0.25em',
                                                    dy: '0.25em',
                                                })}
                                            />
                                            <AxisBottom
                                                top={innerH}
                                                scale={xScale}
                                                numTicks={Math.max(2, Math.floor(innerW / 80))}
                                                stroke="var(--mantine-color-dimmed)"
                                                tickStroke="var(--mantine-color-dimmed)"
                                                tickFormat={(value) => {
                                                    const d = value as Date;
                                                    return d.toLocaleTimeString([], {
                                                        hour: 'numeric',
                                                        hour12: true,
                                                    });
                                                }}
                                                tickLabelProps={() => ({
                                                    fill: 'var(--mantine-color-dimmed)',
                                                    fontSize: 10,
                                                    textAnchor: 'middle',
                                                })}
                                            />
                                        </VisxGroup>
                                    </svg>
                                );
                            }}
                        </ParentSize>
                    </div>
                )}
            </Stack>
        </Card>
    );
};

export default Activity24hChart;
